import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AjaxSelect } from "@/components/AjaxSelect";
import { EyeOff, Eye, FolderTree, Tag, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function ProductBulkBar({ sel, setSel, allMatching, setAllMatching, pageIds, total, filters, opts, onDone }) {
  const [mode, setMode] = useState(null);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const count = allMatching ? total : sel.length;
  const pageAll = pageIds.length > 0 && pageIds.every((id) => sel.includes(id));
  if (!count) return null;

  const run = async (action, v = "") => {
    const label = { hide: "Hide", show: "Show", category: `Move to category “${v}”`, brand: `Set brand “${v}”` }[action];
    if (!window.confirm(`${label} for ${count.toLocaleString()} product(s)?`)) return;
    setBusy(true);
    try {
      const body = allMatching ? { action, value: v, all_matching: true, filters, confirm_all: true } : { action, value: v, ids: sel };
      const { data } = await api.post("/admin/products/bulk", body);
      toast.success(`${data.updated.toLocaleString()} product(s) updated`);
      setSel([]); setAllMatching(false); setMode(null); setValue(""); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk update failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="sticky top-0 z-20 mb-3 rounded-xl border border-accent bg-amber-50 p-3 text-sm flex flex-wrap items-center gap-2 shadow-sm" data-testid="bulk-bar">
      <b className="text-steel" data-testid="bulk-count">{count.toLocaleString()} selected</b>
      {pageAll && !allMatching && total > pageIds.length && (
        <button className="underline text-accent font-semibold" onClick={() => setAllMatching(true)} data-testid="bulk-select-all-matching">
          Select all {total.toLocaleString()} matching products</button>)}
      {allMatching && <span className="text-xs text-muted-foreground" data-testid="bulk-all-matching-note">All products matching the current search &amp; filters</span>}
      <button className="text-xs text-muted-foreground underline" onClick={() => { setSel([]); setAllMatching(false); }} data-testid="bulk-clear">Clear</button>
      <span className="flex-1" />
      {busy && <Loader2 size={16} className="animate-spin" />}
      <Button size="sm" variant="outline" disabled={busy} onClick={() => run("hide")} data-testid="bulk-hide"><EyeOff size={14} className="mr-1" />Hide</Button>
      <Button size="sm" variant="outline" disabled={busy} onClick={() => run("show")} data-testid="bulk-show"><Eye size={14} className="mr-1" />Show</Button>
      <Button size="sm" variant={mode === "category" ? "default" : "outline"} disabled={busy} onClick={() => { setMode(mode === "category" ? null : "category"); setValue(""); }} data-testid="bulk-category"><FolderTree size={14} className="mr-1" />Recategorise</Button>
      <Button size="sm" variant={mode === "brand" ? "default" : "outline"} disabled={busy} onClick={() => { setMode(mode === "brand" ? null : "brand"); setValue(""); }} data-testid="bulk-brand"><Tag size={14} className="mr-1" />Rebrand</Button>
      {mode && (
        <div className="w-full flex flex-wrap items-center gap-2 pt-2 border-t border-amber-200" data-testid="bulk-value-row">
          <span className="text-xs font-semibold">{mode === "category" ? "New category" : "New brand"}:</span>
          <div className="w-64"><AjaxSelect testId="bulk-value" value={value} onChange={setValue}
            options={mode === "category" ? opts.categories : opts.brands} placeholder="Select…" /></div>
          <Button size="sm" disabled={!value || busy} onClick={() => run(mode, value)} className="bg-steel text-white" data-testid="bulk-apply">Apply</Button>
          <button onClick={() => setMode(null)} aria-label="Cancel" data-testid="bulk-cancel"><X size={15} /></button>
        </div>)}
    </div>
  );
}
